import { z } from 'zod';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';
import { DRILL_RULE_CODES } from './rules.js';

/**
 * Tatbikat Master Library kaydı (ADR 0128 §3; ortağın belgesi §3). Şablon şirket verisidir:
 * ofiste yazılır, `drill_library` yeteneğini bildiren gemiye salt okunur iner. Alanların çoğu
 * `content` JSON'undadır; her içerik değişikliği değiştirilemez yeni sürüm satırı açar ve
 * gerçekleşen tatbikat kaydı (Faz 3) bu sürümü sabitler.
 */

/** Sıklık (belge §4: Monthly / Quarterly / Annual / Custom; SOLAS kapalı alan iki ay). */
export const DRILL_FREQUENCIES = [
  'monthly',
  'bimonthly',
  'quarterly',
  'semiannual',
  'annual',
  'custom',
] as const;
export type DrillFrequencyKind = (typeof DRILL_FREQUENCIES)[number];

const FIXED_MONTHS: Readonly<Record<Exclude<DrillFrequencyKind, 'custom'>, number>> = {
  monthly: 1,
  bimonthly: 2,
  quarterly: 3,
  semiannual: 6,
  annual: 12,
};

export interface DrillFrequency {
  kind: DrillFrequencyKind;
  /** Yalnız `custom`: 1–60 ay. */
  months: number | null;
}

export function frequencyMonths(frequency: DrillFrequency): number {
  return frequency.kind === 'custom' ? (frequency.months ?? 0) : FIXED_MONTHS[frequency.kind];
}

/** Ay sayısından sıklık türü (Excel ve varsayılan kütüphane için). */
export function frequencyOfMonths(months: number): DrillFrequency {
  const kind = (Object.keys(FIXED_MONTHS) as Exclude<DrillFrequencyKind, 'custom'>[]).find(
    (k) => FIXED_MONTHS[k] === months,
  );
  return kind ? { kind, months: null } : { kind: 'custom', months };
}

/** Değerlendirme kriteri türü (belge §6): ölçülen süre ya da Satisfactory / Needs Improvement. */
export const DRILL_CRITERION_KINDS = ['time', 'rating'] as const;
export type DrillCriterionKind = (typeof DRILL_CRITERION_KINDS)[number];

export interface DrillCriterion {
  key: string;
  label: string;
  kind: DrillCriterionKind;
}

/** Belge §6'nın yedi kriteri: her varsayılan şablonda. */
export const DEFAULT_PERFORMANCE_CRITERIA: readonly DrillCriterion[] = [
  { key: 'muster_time', label: 'Muster Time', kind: 'time' },
  { key: 'response_time', label: 'Response Time', kind: 'time' },
  { key: 'crew_familiarity', label: 'Crew Familiarity', kind: 'rating' },
  { key: 'communication', label: 'Communication', kind: 'rating' },
  { key: 'equipment_readiness', label: 'Equipment Readiness', kind: 'rating' },
  { key: 'procedure_compliance', label: 'Procedure Compliance', kind: 'rating' },
  { key: 'team_coordination', label: 'Team Coordination', kind: 'rating' },
];

/** Kriter anahtarı: addan türetilir (`Muster Time` → `muster_time`). */
export function criterionKey(label: string): string {
  const key = label
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return /^[a-z]/.test(key) ? key : `c_${key}`.slice(0, 40);
}

/** Drill Code ve kategori kodu: büyük harf, rakam ve `.`, `_`, `-`; şirkette tekil. */
export const DRILL_CODE_PATTERN = /^[A-Z0-9][A-Z0-9._-]{0,29}$/;

export function isDrillCode(value: string): boolean {
  return DRILL_CODE_PATTERN.test(value);
}

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
/** Uzun metin: satır sonları korunur, satır içi boşluk sadeleşir. */
const paragraph = (value: string) =>
  value
    .normalize('NFC')
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim().replace(/[ \t]+/g, ' '))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const requiredText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const requiredParagraph = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(paragraph)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
const optionalParagraph = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(paragraph)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const code = z
  .string(typed('INVALID_TEXT'))
  .transform((value) => value.trim().toUpperCase())
  .pipe(z.string().min(1, 'REQUIRED').regex(DRILL_CODE_PATTERN, 'INVALID_CODE'));
/** Boş öğeler atılır. */
const textList = (maxItems: number, maxLength: number) =>
  z
    .array(z.string(typed('INVALID_TEXT')), typed('INVALID_LIST'))
    .nullish()
    .transform((items) => (items ?? []).map(collapse).filter((item) => item !== ''))
    .pipe(z.array(z.string().max(maxLength, 'TOO_LONG')).max(maxItems, 'TOO_MANY'));
const uuid = z.uuid('INVALID_ID');

const frequencySchema = z
  .strictObject({
    kind: z.enum(DRILL_FREQUENCIES, typed('INVALID_OPTION')),
    months: z
      .number(typed('INVALID_NUMBER'))
      .int('INVALID_NUMBER')
      .min(1, 'OUT_OF_RANGE')
      .max(60, 'OUT_OF_RANGE')
      .nullish()
      .transform((value) => value ?? null),
  })
  .superRefine((value, ctx) => {
    if (value.kind === 'custom' && value.months === null) {
      ctx.addIssue({ code: 'custom', path: ['months'], message: 'REQUIRED' });
    }
  })
  .transform((value) => (value.kind === 'custom' ? value : { kind: value.kind, months: null }));

const criterionSchema = z.strictObject({
  key: z.string(typed('INVALID_TEXT')).regex(/^[a-z][a-z0-9_]{0,39}$/, 'INVALID_KEY'),
  label: requiredText(80),
  kind: z.enum(DRILL_CRITERION_KINDS, typed('INVALID_OPTION')),
});

const roleSchema = z.strictObject({
  role: requiredText(80),
  duty: optionalText(300),
});

export const drillContentSchema = z.strictObject({
  /** Uygulanabilir gemi tipleri; boş: bütün gemiler. */
  vesselTypes: textList(20, 60),
  regulatoryReference: optionalText(300),
  smsProcedure: optionalText(300),
  objective: requiredParagraph(2000),
  scenario: requiredParagraph(4000),
  equipment: textList(60, 200),
  roles: z
    .array(roleSchema, typed('INVALID_LIST'))
    .max(40, 'TOO_MANY')
    .nullish()
    .transform((value) => value ?? []),
  preparation: textList(60, 300),
  steps: textList(80, 300).pipe(z.array(z.string()).min(1, 'REQUIRED')),
  safety: textList(40, 300),
  criteria: z
    .array(criterionSchema, typed('INVALID_LIST'))
    .min(1, 'REQUIRED')
    .max(30, 'TOO_MANY')
    .superRefine((value, ctx) => {
      if (new Set(value.map((c) => c.key)).size !== value.length) {
        ctx.addIssue({ code: 'custom', message: 'DUPLICATE_KEY' });
      }
    }),
  debriefing: textList(40, 300),
  correctiveActions: textList(40, 300),
  reportTemplate: optionalParagraph(4000),
  frequency: frequencySchema,
  /** Varsayılan kural (ADR 0128 §4); boş: kurala bağlı değil (şirketin sıklığı). */
  ruleCode: z
    .string(typed('INVALID_TEXT'))
    .refine((value) => DRILL_RULE_CODES.includes(value), 'INVALID_OPTION')
    .nullish()
    .transform((value) => value ?? null),
});
export type DrillTemplateContent = z.output<typeof drillContentSchema>;

export const drillTemplateSchema = z.strictObject({
  code,
  name: requiredText(120),
  categoryId: uuid,
  subcategoryId: uuid.nullish().transform((value) => value ?? null),
  active: z
    .boolean(typed('INVALID_BOOLEAN'))
    .nullish()
    .transform((value) => value ?? true),
  content: drillContentSchema,
});
export type DrillTemplateInput = z.output<typeof drillTemplateSchema>;

export const drillCategorySchema = z.strictObject({
  code,
  name: requiredText(80),
  parentId: uuid.nullish().transform((value) => value ?? null),
  sortOrder: z
    .number(typed('INVALID_NUMBER'))
    .int('INVALID_NUMBER')
    .min(0, 'OUT_OF_RANGE')
    .max(9999, 'OUT_OF_RANGE')
    .nullish()
    .transform((value) => value ?? 0),
  active: z
    .boolean(typed('INVALID_BOOLEAN'))
    .nullish()
    .transform((value) => value ?? true),
});
export type DrillCategoryInput = z.output<typeof drillCategorySchema>;

function parsed<T>(result: z.ZodSafeParseResult<T>): ParseResult<T> {
  return result.success
    ? { ok: true, value: result.data }
    : {
        ok: false,
        issues: toIssues(result.error).filter(
          (issue, index, all) => all.findIndex((i) => i.path === issue.path) === index,
        ),
      };
}

export function parseDrillTemplate(input: unknown): ParseResult<DrillTemplateInput> {
  return parsed(drillTemplateSchema.safeParse(input));
}

export function parseDrillContent(input: unknown): ParseResult<DrillTemplateContent> {
  return parsed(drillContentSchema.safeParse(input));
}

export function parseDrillCategory(input: unknown): ParseResult<DrillCategoryInput> {
  return parsed(drillCategorySchema.safeParse(input));
}
